<template>
  <card-container
    :id="pageId"
    :is-loading="isLoading"
    loading-message="Loading Live Rides..."
    no-action
  >
    <template #title>
      Live Rides
    </template>

    <template #content>
      <table-view-container
        :fixed-column-counts="3"
        :page-id="pageId"
        :data="liveRides"
        :table-columns="tableColumns"
      />
    </template>
  </card-container>
</template>

<script setup lang="ts">
import { ref, Ref, onMounted } from 'vue';
import _ from 'lodash';

import util from '@/Utils/util';
import { useSwalDialog } from '@/use/swalDialog';
import rideUtility from '@/Utils/rideUtility';
import { TableColumnType } from '@/models/table';
import { sendGet } from '@/Utils/api';

const pageId = 'live-rides';
const tableColumns: TableColumnType[] = [
  { label: 'Customer', field: 'customerName', width: 150 },
  { label: 'Ride ID', field: 'id', width: 100 },
  { label: 'Request ID', field: 'requestId', width: 180 },
  { label: 'Date and Time', field: 'createdAt', width: 180, isDateField: true },
  { label: 'Scheduled Ride ID', field: 'scheduledRideId', width: 140 },
  { label: 'Status', field: 'status', width: 140 },
  { label: 'Service Type', field: 'serviceType', width: 120 },
  { label: 'Caller ID', field: 'callerId', width: 260 },
  { label: 'Custom Rider Name', field: 'customRiderName', width: 150 },
  { label: 'Ride Phone Number', field: 'riderPhone', width: 150 },
  { label: 'Ride Vendor', field: 'vendor', width: 120, isCapitalize: true },
  { label: 'Corp Account Id', field: 'dummyId', width: 140 },
  { label: 'Ride Type', field: 'rideType', width: 100, isCapitalize: true },
  { label: 'Ordered By', field: 'orderedBy', width: 100, isCapitalize: true },
  { label: 'Start Location', field: 'startLocation', width: 220 },
  { label: 'Start Lat', field: 'startLat', width: 100 },
  { label: 'Start Long', field: 'startLong', width: 100 },
  { label: 'Cur Lat', field: 'curLat', width: 100 },
  { label: 'Cur Lng', field: 'curLng', width: 100 },
  { label: 'Last Lat', field: 'lastLat', width: 100 },
  { label: 'Last Lng', field: 'lastLng', width: 100 },
  { label: 'End Location', field: 'endLocation', width: 220 },
  { label: 'End Lat', field: 'endLat', width: 100 },
  { label: 'End Long', field: 'endLong', width: 100 },
  { label: 'Destination', field: 'destination', width: 220 },
  { label: 'Dest Lat', field: 'destinationLat', width: 100 },
  { label: 'Dest Lng', field: 'destinationLng', width: 100 },
  { label: 'End Note', field: 'endNote', width: 220 },
  { label: 'Need Love ID', field: 'needLoveId', width: 100 },
  { label: 'Duration', field: 'duration', width: 100 },
  { label: 'Distance', field: 'distance', width: 100 },
  { label: 'Map', field: 'map', width: 240, isReadMore: true, maxLength: 60 },
  { label: 'Car ID', field: 'carId', width: 80 },
  { label: 'Driver Name', field: 'driverName', width: 120 },
  { label: 'Driver Phone', field: 'driverPhone', width: 120 },
  { label: 'Driver Photo', field: 'driverPhoto', width: 240, isReadMore: true, maxLength: 60 },
  { label: 'Driver Car Image', field: 'driverCarImage', width: 240, isReadMore: true, maxLength: 60 },
  { label: 'Driver Changes', field: 'driverChanges', width: 120 },
  { label: 'Driver Plate', field: 'driverPlate', width: 120 },
  { label: 'Driver Rating', field: 'driverRating', width: 120 },
  { label: 'Driver Cancels', field: 'driverCancels', width: 120 },
  { label: 'Driver Taking a Long Time', field: 'driverTakingLongTime', width: 120 },
  { label: 'Driver Wait More Than 7Mins', field: 'driverWaitMoreThan7Mins', width: 210 },
  { label: 'Driver Joy Ride', field: 'driverJoyRide', width: 120 },
  { label: 'Driver Calls', field: 'driverCalls', width: 100 },
  { label: 'Driver Texts', field: 'driverTexts', width: 110 },
  { label: 'Call Rider Driver Arriving', field: 'callRiderDriverArriving', width: 180 },
  { label: 'Note To Driver', field: 'noteToDriver', width: 260, isReadMore: true, maxLength: 60 },
  { label: 'Time Accepted', field: 'timeAccepted', width: 180, isDateField: true },
  { label: 'Time Arriving', field: 'timeArriving', width: 180, isDateField: true },
  { label: 'Time In Progress', field: 'timeInProgress', width: 180, isDateField: true },
  { label: 'Time Completed', field: 'timeCompleted', width: 180, isDateField: true },
  { label: 'Time Receipt Ready', field: 'timeReceiptReady', width: 180, isDateField: true },
  { label: 'Time Canceled', field: 'timeCanceled', width: 180, isDateField: true },
  { label: 'Time Zone', field: 'timeZone', width: 160 },
  { label: 'ETA', field: 'eta', width: 70 },
  { label: 'Accepted ETA', field: 'acceptedEta', width: 120 },
  { label: 'Digit Pressed', field: 'digitPressed', width: 120 },
  { label: 'Switched Vendors', field: 'switchedVendors', width: 150 },
  { label: 'Skipped Status', field: 'skippedStatus', width: 130 },
  { label: 'Wrong Status', field: 'wrongStatus', width: 120 },
  { label: 'Receipt Call', field: 'receiptCall', width: 120 },
  { label: 'Cost Per Min', field: 'costPerMin', width: 120 },
  { label: 'Cost Per Mile', field: 'costPerMile', width: 120 },
  { label: 'Base Fare', field: 'baseFare', width: 100 },
  { label: 'Distance Fee', field: 'distanceFee', width: 120 },
  { label: 'Time Fee', field: 'timeFee', width: 100 },
  { label: 'Normal Fare', field: 'normalFare', width: 120 },
  { label: 'Booking Fee', field: 'bookingFee', width: 120 },
  { label: 'Minimum Fee', field: 'minimumFee', width: 120 },
  { label: 'Surge Factor', field: 'surgeFactor', width: 120 },
  { label: 'Surge Url', field: 'surgeUrl', width: 120 },
  { label: 'Surge Id', field: 'surgeId', width: 140, isReadMore: true, maxLength: 40 },
  { label: 'Subtotal', field: 'subtotal', width: 100 },
  { label: 'Vendor Total Fare', field: 'totalFare', width: 150 },
  { label: 'GoGoGrandparent Concierge Fee', field: 'gogoRevenue', width: 220 },
  { label: 'GoGo Fee', field: 'gogoFee', width: 100 },
  { label: 'GoGo Mins', field: 'gogoMins', width: 100 },
  { label: 'Total Fare', field: 'gogoCost', width: 100 },
  { label: 'Customer Cost Override', field: 'customerCostOverride', width: 160 },
  { label: 'Customer Cost', field: 'customerCost', width: 120 },
  { label: 'Customer Charge ID', field: 'customerChargeId', width: 240 },
  { label: 'Time Customer Charged', field: 'timeCustomerCharged', width: 180, isDateField: true },
  { label: 'Customer Refund', field: 'customerRefund', width: 140 },
  { label: 'Customer Refund ID', field: 'customerRefundId', width: 150 },
  { label: 'Time Customer Refunded', field: 'timeCustomerRefunded', width: 180, isDateField: true },
  { label: 'Customer Credits', field: 'customerCredits', width: 150 },
  { label: 'Customer Credits Refund', field: 'customerCreditsRefund', width: 180 },
  { label: 'Time Customer Credits Refunded', field: 'timeCustomerCreditsRefunded', width: 220, isDateField: true },
  { label: 'Partner ID', field: 'partnerId', width: 100 },
  { label: 'Partner Cost', field: 'partnerCost', width: 120 },
  { label: 'Partner Charge ID', field: 'partnerChargeId', width: 150 },
  { label: 'Time Partner Charged', field: 'timePartnerCharged', width: 180, isDateField: true },
  { label: 'Partner Refund', field: 'partnerRefund', width: 140 },
  { label: 'Partner Refund Id', field: 'partnerRefundId', width: 150 },
  { label: 'Time Partner Refunded', field: 'timePartnerRefunded', width: 180, isDateField: true },
  { label: 'Partner Credits', field: 'partnerCredits', width: 150 },
  { label: 'Partner Credits Refund', field: 'partnerCreditsRefund', width: 180 },
  { label: 'Time Partner Credits Refunded', field: 'timePartnerCreditsRefunded', width: 220, isDateField: true },
  { label: 'Is Emergency', field: 'isEmergency', width: 120 },
  { label: 'Emergency Reason', field: 'emergencyReason', width: 150 },
  { label: 'Time Customer Credits Used', field: 'timeCustomerCreditsUsed', width: 200, isDateField: true },
  { label: 'Time Partner Credits Used', field: 'timePartnerCreditsUsed', width: 200, isDateField: true },
  { label: 'Reason', field: 'reason', width: 180 },
  { label: 'Redispatches', field: 'redispatches', width: 120 },
  { label: 'Customer Charge Decline', field: 'customerChargeDecline', width: 180, isList: true },
  { label: 'Partner Charge Decline', field: 'partnerChargeDecline', width: 180, isList: true },
  { label: 'Expand Driver Search', field: 'isExpandDriverSearch', width: 170 },
  { label: 'Expand Vehicle Search', field: 'isExpandVehicleSearch', width: 170 },
  {
    label: 'Previous Drivers',
    field: 'previousDrivers',
    width: 280,
    isWrapNormal: true,
    isReadMore: true,
    maxLength: 90,
    moreTxtClass: 'pl-20 mobile-pl-20',
  },
];

const liveRideStatus: string[] = ['start', 'accepted', 'arriving', 'in_progress'];
const paginationLimit = 30;

const { showErrorPopupDialog } = useSwalDialog();

const liveRides: Ref<any> = ref([]);
const isLoading = ref(false);

const loadAllLiveRides = async () => {
  liveRides.value = [];

  isLoading.value = true;
  await getLiveRides();
  isLoading.value = false;

  // sort live rides by id
  liveRides.value = _.orderBy(liveRides.value, ['id'], ['desc']);
};

// This function is recursive one because the API returns response regarding with page limit and we don't know total count
const getLiveRides = async (currentPage: number = 1) => {
  const offset = (currentPage - 1) * paginationLimit;

  try {
    // GET /rides?status=start,accepted,arriving,in_progress&offset=***&limit=30
    const response = await sendGet('rides', { status: liveRideStatus.join(','), offset, limit: paginationLimit });

    response.data.rows.map((ride: any) => {
      ride = rideUtility.formatRideData(ride);

      liveRides.value.push(ride);
    });

    // Until total count is reached to the count
    if (liveRides.value.length < response.data.count) {
      await getLiveRides(currentPage + 1);
    }
  } catch (error) {
    const errorText = util.getErrorResponse(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  }
};

onMounted(() => {
  util.moveToTop();
  loadAllLiveRides();
});
</script>
